import { useEffect, useState } from "react";

import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import dayjs from "dayjs";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash-es/isEmpty";

import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";

import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import Box from "@mui/material/Box";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";
import SubTitle from "../../components/SubTitle";

import { doService } from "../../services/ServiceHelper";
import StudentsSelector from "../../components/faculty/report/StudentsSelector";
import {
  StripedTableRow,
  StripedTableCell,
} from "../../components/StripedTable";
import TableRowNoResults from "../../components/TableRowNoResults";

import StudentListSelector from "../../components/faculty/report/StudentListSelector";
import { showDialog, showOKCancelDialog } from "../../components/ATPDialogs";

import {
  deleteScores,
  getPreviousScores,
  updateScoreValue,
} from "../../services/ScoreService";
import { getUserListMembers_v2 } from "../../services/UserService";

const ManageScores = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_searchPerformed, setSearchPerformed] = useState(false);

  const [_dateRange, setDateRange] = useState([dayjs(), dayjs()]);

  const [_results, setResults] = useState([]);
  const [_selectedStudents, setSelectedStudents] = useState([]);
  const [_selectAll, setSelectAll] = useState(false);
  const [_studentRange, setStudentRange] = useState("XX");

  useEffect(() => {
  }, []);

  const handleChangeDateRange = (value) => setDateRange(value);

  const handleSearch = () => {
    const dateStart = _dateRange[0].format("YYYY-MM-DD");
    const dateEnd = _dateRange[1].format("YYYY-MM-DD");

    const studentEids = [];

    for (let selectedStudent of _selectedStudents) {
      studentEids.push(selectedStudent.value);
    }

    if (_studentRange === "S" && studentEids.length === 0) {
      showDialog("Unable to Search", "Please select 1 or more students.");
      return;
    }

    setShowProgress(true);
    doService(
      getPreviousScores(_school, _jwt, studentEids, dateStart, dateEnd),
      (results) => {
        const relevantResults = [];

        // flatten out results to be displayed
        for (let result of results.data) {
          if (result.scores.length > 0) {
            // flatten results
            for (let score of result.scores) {
              relevantResults.push({
                eid: score["eid"],
                last_name: result["last_name"],
                first_name: result["first_name"],
                grade: result["grade"],
                student_eid: result["student_eid"],
                scoreDate: score["scoreDate"],
                dateCreated: score["dateCreated"],
                categoryName: score["categoryName"],
                scoreValue: score["scoreValue"],
                conName: score["conName"],
                comment: score["comment"],
                teacherLastName: score["teacherLastName"],
                teacherFirstName: score["teacherFirstName"],
                checked: false,
              });
            }
          }
        }
        setShowProgress(false);
        setSearchPerformed(true);
        setResults(relevantResults);
      }
    );
  };

  const onSelectStudents = (studentEids) => {
    setSelectedStudents(studentEids);
  };

  const handleChangeSelectAll = (event) => {
    setSelectAll(event.target.checked);

    // update selected checkboxes
    const newResults = cloneDeep(_results);
    for (let result of newResults) {
      result.checked = event.target.checked;
    }

    setResults(newResults);
  };

  const handleClickDelete = () => {
    // get clicked items
    const toDeleteEids = [];
    for (let result of _results) {
      if (result.checked) {
        toDeleteEids.push(result.eid);
      }
    }

    if (toDeleteEids.length === 0) {
      showDialog("Unable to Delete", "Please select 1 or more scores.");
      return;
    }

    showOKCancelDialog(
      "Confirm Delete",
      "WARNING: You are about to PERMANENTLY delete student scores.  Click OK to proceed.",
      () => {
        setShowProgress(true);
        doService(deleteScores(_school, _jwt, toDeleteEids), (results) => {
          handleSearch();
          window.showSnack("Scores Deleted.");
          setShowProgress(false);
        });
      }
    );
  };

  const onSelectList = (value) => {
    setShowProgress(true);
    doService(getUserListMembers_v2(_school, _jwt, value), (results) => {
      const students = cloneDeep(_selectedStudents);

      // add these members to the student list
      for (let result of results.data) {
        students.push({
          label: `${result.last_name}, ${result.first_name} (${result.grade})`,
          value: result.eid,
        });
      }

      setSelectedStudents(students);
      setShowProgress(false);
    });
  };

  const handleChangeSelect = (event, result) => {
    const results = [..._results];

    for (let r of results) {
      if (r.eid === result.eid) {
        r.checked = event.target.checked;
        break;
      }
    }

    setResults(results);
  };

  const handleChangeScoreValue = (event, result) => {
    const results = [..._results];

    for (let r of results) {
      if (r.eid === result.eid) {
        r.scoreValue = event.target.value;
        doUpdateScore(r.eid, r.scoreValue);
        break;
      }
    }

    setResults(results);
  };

  const doUpdateScore = (eid, scoreValue) => {
    setShowProgress(true);
    doService(updateScoreValue(_school, _jwt, eid, scoreValue), (results) => {
      setShowProgress(false);
      window.showSnack("Score Updated.");
    });
  };

  const handleChangeStudentRange = (event) => {
    setStudentRange(event.target.value);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Manage Scores" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Alert severity="info" sx={{ pb: 0, mt: 1 }}>
          Look up the scores you created. You can delete them, if needed.
        </Alert>

        <Box sx={{ boxShadow: 1, p: 1, mt: 1 }}>
          <SubTitle text="Search Criteria" />

          <InputLabel>Start and End Dates *:</InputLabel>

          <MobileDateRangePicker
            defaultValue={[dayjs(), dayjs()]}
            value={_dateRange}
            sx={{ mt: 1, mb: 2 }}
            onChange={handleChangeDateRange}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputLabel>Student Range *</InputLabel>
              <Select
                onChange={(event) => handleChangeStudentRange(event)}
                value={_studentRange}
                size="small"
                sx={{ mb: 1 }}
                fullWidth
              >
                <MenuItem key="XX" value="XX">
                  -- ALL STUDENTS --
                </MenuItem>
                <MenuItem key="S" value="S">
                  -- SOME STUDENTS --
                </MenuItem>
              </Select>

              {_studentRange === "S" && (
                <StudentsSelector
                  school={_school}
                  jwt={_jwt}
                  onChange={onSelectStudents}
                  selectedStudents={_selectedStudents}
                  showLabel={false}
                />
                )}
            </Grid>
            <Grid item xs={6} sx={{ mt: 0, pt: 0 }}>
              <StudentListSelector
                school={_school}
                jwt={_jwt}
                onChange={onSelectList}
              />
            </Grid>
          </Grid>

          <Button
            sx={{ mt: 1 }}
            variant="contained"
            fullWidth
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>

        {_searchPerformed && (
          <Box sx={{ boxShadow: 1, p: 1, mt: 2 }}>
            <SubTitle text={`Student Scores (${_results.length})`} />

            <Grid container spacing={2} sx={{ mt: 0.0 }}>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(event) => handleChangeSelectAll(event)}
                      checked={_selectAll}
                    />
                  }
                  label={"Select/Deselect All"}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  sx={{ mt: 1 }}
                  variant="outlined"
                  fullWidth
                  onClick={handleClickDelete}
                >
                  Clear Selected Scores
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mt: 2, mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <StripedTableRow>
                    <StripedTableCell>Student Name</StripedTableCell>
                    <StripedTableCell align="center">Date</StripedTableCell>
                    <StripedTableCell>Category</StripedTableCell>
                    <StripedTableCell>Consequence</StripedTableCell>
                    <StripedTableCell>Created By +<br/>Comments</StripedTableCell>
                    <StripedTableCell align="center" width={110}>Created</StripedTableCell>
                    <StripedTableCell>Score</StripedTableCell>
                  </StripedTableRow>
                </TableHead>
                <TableBody>
                  <TableRowNoResults data={_results} />

                  {_results.map((result) => (
                    <StripedTableRow>
                      <StripedTableCell>
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={(event) =>
                                handleChangeSelect(event, result)
                              }
                              checked={result.checked}
                            />
                          }
                          label={`${result.last_name}, ${result.first_name} (${result.grade})`}
                        />
                      </StripedTableCell>
                      <StripedTableCell align="center">
                        {dayjs(result.scoreDate).format("MM/DD")}
                      </StripedTableCell>
                      <StripedTableCell>{result.categoryName}</StripedTableCell>
                      <StripedTableCell>
                        {isEmpty(result.conName) ? "--NONE--" : result.conName}
                      </StripedTableCell>
                      <StripedTableCell>
                        <b>{result.teacherLastName}, {result.teacherFirstName}</b>
                        <br />
                        {isEmpty(result.comment) ? "--NONE--" : result.comment}
                      </StripedTableCell>
                      <StripedTableCell align="center">
                        {dayjs(result.dateCreated).format("MM/DD hh:mm a")}
                      </StripedTableCell>
                      <StripedTableCell>
                        <Select
                          onChange={(event) =>
                            handleChangeScoreValue(event, result)
                          }
                          value={result.scoreValue}
                          size="small"
                        >
                          <MenuItem key="0" value="0">
                            0
                          </MenuItem>
                          <MenuItem key="1" value="1">
                            1
                          </MenuItem>
                          <MenuItem key="2" value="2">
                            2
                          </MenuItem>
                          <MenuItem key="3" value="3">
                            3
                          </MenuItem>
                          <MenuItem key="4" value="4">
                            4
                          </MenuItem>
                          <MenuItem key="5" value="5">
                            5
                          </MenuItem>
                        </Select>
                      </StripedTableCell>
                    </StripedTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Container>
    </>
  );
};

export default ManageScores;
